import { cfetch } from '@confluence/network';
import type { BadStatusError } from '@confluence/network';

import { throwErrorWithResponse } from './utils/throwErrorWithResponse';

export type FetchOptions<TVariables, TResponse> = {
	url: string | ((variables: TVariables) => string);
	method?: string | ((variables: TVariables) => string);
	headers?: HeadersInit;
	body?: BodyInit | ((variables: TVariables) => BodyInit);
	credentials?: RequestCredentials;
	parseResponseOnSuccess?: (response: Response) => TResponse | Promise<TResponse>;
	parseResponseOnError?: (
		error: Error | BadStatusError,
		variables: TVariables,
	) => TResponse | Promise<TResponse>;
};

export async function fetchResponse<TVariables, TResponse>(
	fetchOptions: FetchOptions<TVariables, TResponse>,
	variables: TVariables,
): Promise<TResponse> {
	const { url, method, body, headers, credentials, parseResponseOnSuccess, parseResponseOnError } =
		fetchOptions;
	try {
		const resolvedMethod = typeof method === 'function' ? method(variables) : method;
		const defaultBody = resolvedMethod === 'GET' ? undefined : JSON.stringify(variables);

		const response = await cfetch(typeof url === 'function' ? url(variables) : url, {
			method: resolvedMethod,
			body: typeof body === 'function' ? body(variables) : body || defaultBody,
			headers,
			credentials,
		});

		if (parseResponseOnSuccess) {
			return parseResponseOnSuccess(response);
		}

		return await parseResponse(response);
	} catch (error) {
		if (parseResponseOnError) {
			return parseResponseOnError(error, variables);
		}
		throw error;
	}
}

async function parseResponse<TResponse>(response: Response): Promise<TResponse> {
	try {
		return await response.json();
	} catch (error) {
		// Only handle empty body errors (common from APIs returning 200/204 with no content)
		// Let actual JSON parsing errors (malformed JSON) propagate
		if (error.message && error.message.includes('Unexpected end of JSON input')) {
			return {} as TResponse;
		}
		throw throwErrorWithResponse(error, response);
	}
}
